import React from 'react';
import * as Yup from 'yup';
import { Box, Avatar, Chip, List, ListItemAvatar, ListItemButton, ListItemText, Typography, InputAdornment, IconButton } from '@mui/material';
import { ScrollableView } from '../Layout';
import { useCreateUserConversationMutation, useUserConversationsQuery } from '@uplift/queries';
import { Color } from './types';
import { Button } from '../Button';
import { Add, Clear, Search } from '@mui/icons-material';
import { ModalActions, ModalContent, ModalTitle } from '../Modal';
import { PatientSearchAutocomplete } from '../molecules/Forms/AddSessionForm/components/UserInfo/PatientsAutocomplete.component';
import { CancelButton, FormModal, SubmitButton } from '../Form';
import { Formik } from 'formik';
import { Conversation } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { UserRole } from '@uplift/types';
import { ProviderSearchAutocomplete } from './ProviderSearchAutocomplete.component';
import { StyledClientsSearchInputBase } from '../Layout/ProviderLayout/PatientSearch';
import { ConversationItemSubtitle } from './Messages/Header.component';
import { AdminSearchAutocomplete } from './AdminSearchAutocomplete.component';


export type ConversationItemProps = {
  conversation: Conversation;
  selected?: boolean;
  onSelect?: () => void;
  color: Color;
};


const ConversationItem: React.VFC<ConversationItemProps> = ({ conversation, selected, onSelect, color }) => {
  const unreadMessagesCount = conversation.unreadMessagesCount || 0;

  return (
    <ListItemButton alignItems="center" selected={selected} onClick={onSelect} data-testid="conversation-item">
      <ListItemAvatar>
        <Avatar>{conversation.name[0]}</Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={conversation.name}
        secondary={<ConversationItemSubtitle color={color} conversation={conversation} />}
      />
      {unreadMessagesCount ? <Chip color={color} label={unreadMessagesCount} size="small" /> : null}
    </ListItemButton>
  );
};


const AddConversationButton: React.FC<{
  opened: boolean;
  color: Color;
  setOpened: (opened: boolean) => void;
  handleConversationSelectById: (conversationId: string) => void;
}> = ({ color, opened, setOpened, handleConversationSelectById }) => {
  const me = useCurrentuser();
  const createConversationMutation = useCreateUserConversationMutation();

  return (
    <>
      <Button size="large" color={color} fullWidth startIcon={<Add />} onClick={() => setOpened(true)} data-testid="add-conversation-button">
        Add Conversation
      </Button>
      <Formik
        initialValues={{ participant: null } as { participant: { id: string } | null }}
        validationSchema={Yup.object().shape({
          participant: Yup.object().shape({
            id: Yup.string().uuid('Participant must be a valid user'),
          }).nullable().required('Participant is required'),
        })}
        onSubmit={async (values) => {
          if (values.participant) {
            const result = await createConversationMutation.mutateAsync({ participantId: values.participant.id });

            setOpened(false);
            handleConversationSelectById(result.data.conversationId);
          }
        }}
      >
        <FormModal open={opened} onClose={() => setOpened(false)} maxWidth="sm" fullWidth data-testid="add-conversation-modal">
          <ModalTitle onClose={() => setOpened(false)}>Select a participant</ModalTitle>
          <ModalContent>
            {me.role === UserRole.provider && <PatientSearchAutocomplete data-testid="participant-search-autocomplete" name="participant" disablePortal={false} />}
            {me.role === UserRole.patient && <ProviderSearchAutocomplete data-testid="participant-search-autocomplete" name="participant" disablePortal={false} />}
            {me.role === UserRole.superadmin && <AdminSearchAutocomplete data-testid="participant-search-autocomplete" name="participant" disablePortal={false} />}
          </ModalContent>
          <ModalActions>
            <CancelButton onClick={() => setOpened(false)} data-testid="cancel-add-participant-button">Close</CancelButton>
            <SubmitButton disabledOnInvalid color={color} data-testid="submit-add-participant-button">Add</SubmitButton>
          </ModalActions>
        </FormModal>
      </Formik>
    </>
  );
};


export type ConversationsProps = {
  color?: Color;
  selectedConversationId?: string;
  setSelectedConversationId: (conversation: string) => void;
};


export const ConversationsList: React.FC<ConversationsProps> = ({
  selectedConversationId,
  setSelectedConversationId,
  color = 'secondary'
}) => {
  const [ nameFilter, setNameFilter ] = React.useState('');
  const [ newConversationModalOpened, setNewConversationModalOpened ] = React.useState(false);
  const { data: _conversations = [] } = useUserConversationsQuery();
  const selectedConversation = _conversations.find(c => c.id === selectedConversationId);
  const filteredConversations = _conversations.filter(c => {
    const name = c.name.toLowerCase();

    return name.includes(nameFilter.toLowerCase());
  });

  return (
    <Box display="flex" flexDirection="column" height="100%" width="100%">
      <Box padding={2} pb={0}>
        <StyledClientsSearchInputBase
          data-testid="conversation-search-input"
          color={color}
          value={nameFilter}
          onChange={e => setNameFilter(e.target.value)}
          startAdornment={<InputAdornment position="start"><Search color="disabled" /></InputAdornment>}
          endAdornment={nameFilter && (
            <InputAdornment position="start">
              <IconButton size="small" onClick={() => setNameFilter('')}>
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          )}
          placeholder="Search..."
        />
      </Box>
      <ScrollableView width="100%" height="100%">
        <List sx={{ width: '100%', minHeight: '100%', boxSizing: 'border-box' }}>
          {!_conversations.length && (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body1" align="center" color="textSecondary" sx={{ color: 'grey.400', fontWeight: 500 }}>
                No conversations. <br />
                Create new conversation to start messaging
              </Typography>
            </Box>
          )}
          {!!_conversations.length && !filteredConversations.length && (
            <Box display="flex" flexDirection="column" alignItems="center" pt={3} height="100%">
              <Typography variant="body1" align="center" color="textSecondary" sx={{ color: 'grey.400', fontWeight: 500 }}>
                No conversations Found. <br />
              </Typography>
              <Box pt={1}>
                <Button
                  variant="text"
                  color={color}
                  onClick={() => {
                    setNewConversationModalOpened(true);
                    setNameFilter('');
                  }}
                  startIcon={<Add />}
                >
                  Add new conversation
                </Button>
              </Box>
            </Box>
          )}
          {filteredConversations.map((conversation, index) => (
            <ConversationItem
              key={index}
              color={color}
              conversation={conversation}
              selected={conversation === selectedConversation}
              onSelect={() => {
                setSelectedConversationId(conversation.id);
              }}
            />
          ))}
        </List>
      </ScrollableView>
      <AddConversationButton
        color={color}
        opened={newConversationModalOpened}
        setOpened={setNewConversationModalOpened}
        handleConversationSelectById={conversationId => {
          setSelectedConversationId(conversationId);
          setNameFilter('');
        }}
      />
    </Box>
  );
};
